/**
 * The viewport panel: the render canvas plus everything drawn over it (toolbar,
 * labels, selection bracket, axis triad, navigation hint), input routing (the
 * mouse and pen to the ViewportNavigator, fingers to the TouchNavigator), the
 * context menu an RMB click or a long press on an entity opens, and the
 * marquee an LMB drag draws.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DragEvent, PointerEvent as ReactPointerEvent } from "react";
import { droppedAsset, isAssetDrag } from "../create/assetDrag";
import { assetPreset } from "../create/createModel";
import { useCreate, useLanding } from "../create/useCreate";
import type { CreatePlace } from "../create/useCreate";
import { runEdit, useEditor } from "../EditorContext";
import { marqueeHits, selectionAfterMarquee } from "../selection/marquee";
import type { MarqueeMode, Rect } from "../selection/marquee";
import { setVisibleEdit, visibilityOf } from "../selection/selectionEdits";
import { entityEditItems, useEntityEdits } from "../selection/entityEdits";
import { cameraMenuItems } from "./cameraMenuItems";
import { groundPoint, screenRay } from "./cameraMath";
import { screenRectOfBounds } from "./overlayLayout";
import { ViewportNavigator } from "./ViewportNavigator";
import type { ViewportNavigatorOptions } from "./ViewportNavigator";
import { TouchNavigator } from "./TouchNavigator";
import type { TouchNavigatorOptions } from "./TouchNavigator";
import { systemTimers } from "../../core/Timers";
import { swipeStep } from "../play/playSwipe";
import { ViewportOverlays } from "./ViewportOverlays";
import { ViewportSurface } from "./ViewportSurface";
import { LevelTransition } from "../level-transition/LevelTransition";
import { PlayOverlay } from "../play/PlayOverlay";
import { useTourTarget } from "../tour/TourTargets";
import { useContextMenu } from "../ui/ContextMenu";
import type { ContextMenuHandle } from "../ui/ContextMenu";
import { ViewportToolbar } from "./ViewportToolbar";
import "./viewport.css";

export function ViewportPanel() {
  const { render, play } = useEditor();
  const menu = useContextMenu();
  const openEntityMenu = useEntityMenu(menu.open);
  const openViewMenu = useViewMenu(menu.open);
  const [marquee, setMarquee] = useState<Rect | null>(null);
  const selectInMarquee = useMarqueeSelect();
  const navigator = useNavigator({
    onContextClick: openEntityMenu,
    onViewMenu: openViewMenu,
    onMarquee: setMarquee,
    onMarqueeEnd: selectInMarquee,
  });
  const hostRef = useRef<HTMLDivElement>(null);
  const tourTarget = useTourTarget("viewport");

  const rect = (): ViewRect =>
    hostRef.current?.getBoundingClientRect() ?? { left: 0, top: 0, width: 1, height: 1 };
  const assetDrop = useAssetDrop(rect);
  const touch = useTouchInput({ onLongPress: openEntityMenu, onViewMenu: openViewMenu }, rect);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "touch") {
      touch.down(event);
      return;
    }
    // Only presses on the canvas navigate; overlay buttons handle their own clicks.
    if (!(event.target instanceof HTMLCanvasElement)) return;
    // While playing, the game mode has the view.
    if (play.getSnapshot()) return;
    // The gizmo saw this press first (it listens on the canvas) and owns the drag.
    if (render.backend.gizmoActive) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.focus();
    navigator.pointerDown(event, rect());
    // Global shortcuts leave WASD/QE alone while flying (see shell/shortcuts.ts).
    event.currentTarget.dataset.flying = String(navigator.isFlying);
  };

  return (
    <div
      ref={(element) => {
        hostRef.current = element;
        tourTarget(element);
      }}
      className="kn-viewport-panel"
      tabIndex={-1}
      aria-label="Viewport"
      onPointerDown={onPointerDown}
      onPointerMove={(event) => {
        if (event.pointerType === "touch") touch.move(event);
        else navigator.pointerMove(event);
      }}
      onPointerUp={(event) => {
        if (event.pointerType === "touch") {
          touch.up(event);
          return;
        }
        event.currentTarget.dataset.flying = "false";
        void navigator.pointerUp(event, rect());
      }}
      onPointerCancel={(event) => {
        if (event.pointerType === "touch") touch.cancel(event);
      }}
      onWheel={(event) => {
        if (!play.getSnapshot()) navigator.wheel(event.deltaY);
      }}
      onContextMenu={(event) => {
        event.preventDefault();
      }}
      onDragOver={assetDrop.onDragOver}
      onDragLeave={assetDrop.onDragLeave}
      onDrop={assetDrop.onDrop}
    >
      <ViewportSurface />
      <ViewportOverlays />
      <ViewportToolbar />
      <LevelTransition />
      <PlayOverlay />
      {marquee && (
        <div
          className="kn-marquee"
          style={{
            left: marquee.left,
            top: marquee.top,
            width: marquee.width,
            height: marquee.height,
          }}
        />
      )}
      {menu.element}
    </div>
  );
}

/**
 * Fingers on the viewport: gestures through the TouchNavigator, or, while
 * playing, a sideways swipe to the next or previous zone (the game has the
 * camera, as it does for the mouse).
 */
function useTouchInput(
  menus: Pick<TouchNavigatorOptions, "onLongPress" | "onViewMenu">,
  rect: () => ViewRect,
) {
  const { onLongPress, onViewMenu } = menus;
  const { camera, dispatch, bridge, render, selection, play, settings } = useEditor();
  const swipeStart = useRef<{ x: number; y: number } | null>(null);

  const navigator = useMemo(
    () =>
      new TouchNavigator({
        camera,
        timers: systemTimers,
        dispatch,
        selection,
        pick: (x, y) => bridge.pick(x, y),
        onLongPress,
        onViewMenu,
        onNavigated: () => {
          render.frameLoop.noteInput();
          if (!settings.getSnapshot().navHintDismissed) {
            void dispatch({
              type: "editor.setSetting",
              payload: { key: "navHintDismissed", value: true },
              origin: "viewport",
            });
          }
        },
      }),
    [camera, dispatch, selection, bridge, render, settings, onLongPress, onViewMenu],
  );

  return {
    down: (event: ReactPointerEvent<HTMLDivElement>) => {
      if (play.getSnapshot()) {
        swipeStart.current = { x: event.clientX, y: event.clientY };
        return;
      }
      // The first finger must land on the canvas (overlay buttons take their own taps); a
      // second joins the gesture wherever it lands. The gizmo keeps a press it saw first.
      if (!navigator.active && !(event.target instanceof HTMLCanvasElement)) return;
      if (render.backend.gizmoActive) return;
      navigator.down(event, rect());
    },
    move: (event: ReactPointerEvent<HTMLDivElement>) => {
      navigator.move(event);
    },
    up: (event: ReactPointerEvent<HTMLDivElement>) => {
      const start = swipeStart.current;
      swipeStart.current = null;
      const playing = play.getSnapshot();
      if (start && playing) {
        const step = swipeStep(event.clientX - start.x, event.clientY - start.y);
        if (step !== 0) {
          void dispatch({
            type: "play.goTo",
            payload: { index: playing.current + step },
            origin: "viewport",
          });
        }
        return;
      }
      navigator.up(event, rect());
    },
    cancel: (event: ReactPointerEvent<HTMLDivElement>) => {
      swipeStart.current = null;
      navigator.cancel(event);
    },
  };
}

/** The viewport's box on the page, in CSS pixels. */
interface ViewRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Dropping an asset from the Content Browser places it where the pointer's
 * ray meets the ground, as Unreal drops onto the surface under the cursor
 * (here the ground plane; the camera's focus point when the ray misses it).
 * While the drag is over the viewport, a ghost of the model shows where the
 * drop would put it (PlacementPreview).
 */
function useAssetDrop(rect: () => ViewRect) {
  const { camera, content, placement } = useEditor();
  const create = useCreate("viewport");
  const land = useLanding();

  const placeAt = (event: DragEvent<HTMLDivElement>): CreatePlace => {
    const box = rect();
    const ray = screenRay(
      camera.getSnapshot(),
      event.clientX - box.left,
      event.clientY - box.top,
      box.width,
      box.height,
    );
    const ground = groundPoint(ray);
    return ground ? { at: ground } : {};
  };

  return {
    onDragOver: (event: DragEvent<HTMLDivElement>) => {
      if (!isAssetDrag(event.dataTransfer)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
      placement.moveTo(land(placeAt(event)).world);
    },
    onDragLeave: (event: DragEvent<HTMLDivElement>) => {
      // Moving onto the canvas or an overlay inside the viewport isn't leaving it.
      const into = event.relatedTarget;
      if (into instanceof Node && event.currentTarget.contains(into)) return;
      placement.hide();
    },
    onDrop: (event: DragEvent<HTMLDivElement>) => {
      placement.end();
      const path = droppedAsset(event.dataTransfer);
      const asset = content.index.assets.find((candidate) => candidate.path === path);
      if (!asset) return;
      event.preventDefault();
      create(assetPreset(asset), placeAt(event));
    },
  };
}

/**
 * The selected entity's context menu (an RMB click has just selected it).
 * Stable across renders, so the navigator that calls it isn't rebuilt.
 */
function useEntityMenu(open: ContextMenuHandle["open"]) {
  const { selection, world, dispatch, dispatchAll } = useEditor();
  const editsFor = useEntityEdits("viewport");

  return useCallback(
    (at: { x: number; y: number }) => {
      const acting = selection.getSnapshot();
      const { ids, active } = acting;
      const entity = active === null ? undefined : world.get(active);
      if (!entity) return;

      const { parent } = entity;
      const several = ids.length > 1;
      open(at, several ? `${ids.length} entities` : entity.name, [
        {
          label: "Frame",
          shortcut: "F",
          run: () => void dispatch({ type: "camera.frame", payload: {}, origin: "viewport" }),
        },
        ...(several
          ? []
          : cameraMenuItems(entity, (request) => {
              void dispatch({ ...request, origin: "viewport" });
            })),
        {
          label: entity.visible ? "Hide" : "Show",
          shortcut: "H",
          run: () => {
            const edit = setVisibleEdit(visibilityOf(world, ids), !entity.visible, "viewport");
            runEdit({ dispatch, dispatchAll }, edit);
          },
        },
        {
          label: "Select parent",
          disabled: parent === null || several,
          run: () => {
            if (parent !== null) {
              void dispatch({
                type: "selection.set",
                payload: { ids: [parent] },
                origin: "viewport",
              });
            }
          },
        },
        ...entityEditItems(editsFor(acting)),
      ]);
    },
    [open, editsFor, selection, world, dispatch, dispatchAll],
  );
}

/**
 * The viewport's own menu, where a right-click or a long press lands on empty
 * space. Reset view is the way back for a visitor who's lost the scene (a
 * phone's orbit can end up under the map). Stable across renders.
 */
function useViewMenu(open: ContextMenuHandle["open"]) {
  const { dispatch } = useEditor();

  return useCallback(
    (at: { x: number; y: number }) => {
      open(at, "Viewport", [
        {
          label: "Reset view",
          run: () => void dispatch({ type: "camera.reset", payload: {}, origin: "viewport" }),
        },
      ]);
    },
    [open, dispatch],
  );
}

/**
 * Applies a finished marquee: each visible entity's own bounds, projected,
 * against the rectangle, touching or (with the strict preference) contained.
 */
function useMarqueeSelect() {
  const { bridge, camera, world, selection, settings, dispatch } = useEditor();

  return useCallback(
    (rect: Rect, mode: MarqueeMode) => {
      const { width, height } = camera.viewportSize;
      const view = camera.getSnapshot();
      const candidates = [...world.getSnapshot().entities.keys()].flatMap((id) => {
        const bounds = bridge.isVisible(id) ? bridge.ownBoundsOf(id) : null;
        const screen = bounds ? screenRectOfBounds(view, bounds, width, height) : null;
        return screen ? [{ id, rect: screen }] : [];
      });

      const coverage = settings.getSnapshot().strictBoxSelection ? "contain" : "intersect";
      const hits = marqueeHits(candidates, rect, coverage);
      const next = selectionAfterMarquee(selection.getSnapshot(), hits, mode);
      void dispatch({
        type: "selection.set",
        payload: { ids: next.ids, ...(next.active !== null && { active: next.active }) },
        origin: "viewport",
      });
    },
    [bridge, camera, world, selection, settings, dispatch],
  );
}

/** The navigator's hooks back into the panel (menus, the marquee). */
type NavigatorCallbacks = Pick<
  ViewportNavigatorOptions,
  "onContextClick" | "onViewMenu" | "onMarquee" | "onMarqueeEnd"
>;

/** One navigator per panel, wired to the frame loop and window key events. */
function useNavigator({
  onContextClick,
  onViewMenu,
  onMarquee,
  onMarqueeEnd,
}: NavigatorCallbacks): ViewportNavigator {
  const { camera, settings, dispatch, bridge, render, selection } = useEditor();

  const navigator = useMemo(
    () =>
      new ViewportNavigator({
        camera,
        settings,
        selection,
        dispatch,
        pick: (x, y) => bridge.pick(x, y),
        requestAnimation: () => render.frameLoop.beginAnimation("fly"),
        onContextClick,
        onViewMenu,
        onMarquee,
        onMarqueeEnd,
        onNavigated: () => {
          render.frameLoop.noteInput();
          if (!settings.getSnapshot().navHintDismissed) {
            void dispatch({
              type: "editor.setSetting",
              payload: { key: "navHintDismissed", value: true },
              origin: "viewport",
            });
          }
        },
      }),
    [
      camera,
      settings,
      selection,
      dispatch,
      bridge,
      render,
      onContextClick,
      onViewMenu,
      onMarquee,
      onMarqueeEnd,
    ],
  );

  useEffect(() => {
    const offTick = render.frameLoop.register("input", (frame) => {
      navigator.tick(frame.dt);
    });
    const onKeyDown = (event: KeyboardEvent) => {
      // Shift is tracked even before a flight starts, so holding it first still boosts.
      if (navigator.isFlying || event.key === "Shift") navigator.keyDown(event.key);
    };
    const onKeyUp = (event: KeyboardEvent) => {
      navigator.keyUp(event.key);
    };
    const onBlur = () => {
      navigator.releaseAll();
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      offTick();
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [navigator, render]);

  return navigator;
}
